import api from '@/lib/api';
import {
  WorkoutReadDTO,
  WorkoutCreateDTO,
  Workout,
} from '@/types/workout';
import {
  workoutReadDTOToWorkout,
  workoutToWorkoutCreateDTO,
} from '@/lib/transformers';

const API_BASE_URL = import.meta.env.VITE_API_URL;

class WorkoutApi {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/api/v1/workouts`;
  }

  /**
   * Get all workouts for the current user
   */
  async getWorkouts(): Promise<Workout[]> {
    const response = await api.get<WorkoutReadDTO[]>(`${this.baseURL}/`);
    return response.data.map(workoutReadDTOToWorkout);
  }

  /**
   * Get a specific workout by ID
   */
  async getWorkout(workoutId: string): Promise<Workout> {
    const response = await api.get<WorkoutReadDTO>(`${this.baseURL}/${workoutId}`);
    return workoutReadDTOToWorkout(response.data);
  }

  /**
   * Create a new workout
   */
  async createWorkout(workout: Workout): Promise<Workout> {
    const dto = workoutToWorkoutCreateDTO(workout);
    const response = await api.post<WorkoutReadDTO>(this.baseURL, dto);
    return workoutReadDTOToWorkout(response.data);
  }

  /**
   * Update an existing workout
   */
  async updateWorkout(workoutId: string, workout: Workout): Promise<Workout> {
    const dto = workoutToWorkoutCreateDTO(workout);
    const response = await api.put<WorkoutReadDTO>(`${this.baseURL}/${workoutId}`, dto);
    return workoutReadDTOToWorkout(response.data);
  }

  /**
   * Delete a workout
   */
  async deleteWorkout(workoutId: string): Promise<void> {
    await api.delete(`${this.baseURL}/${workoutId}`);
  }

  /**
   * Generate a workout using the gym agent
   */
  async generateWorkout(prompt: string): Promise<Workout> {
    const response = await api.post<WorkoutReadDTO>(
      `${this.baseURL}/generate_workout`,
      { prompt },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return workoutReadDTOToWorkout(response.data);
  }
}

export const workoutApi = new WorkoutApi();

